// @flow
import React, { Component } from 'react'
import { connect } from 'react-redux'
import Helmet from 'react-helmet'
import { Link } from 'react-router'
import styled from 'styled-components'
import titleCase from 'title-case'
import includes from 'lodash/includes'
import isEmpty from 'lodash/isEmpty'
import get from 'lodash/get'
import { User } from '../../types'
import colors from '../../styles/colors'
import { rebateAmount, padCommas, toUSD, pluralize } from '../../utils'
import { fetchProperty } from '../../actions/properties'
import { createFavorite, deleteFavorite } from '../../actions/user'
import { alert } from '../../actions/alert'
import { openContactModal } from '../../actions/modals'
import Card from '../../components/Card'
import RebateButton from '../../components/RebateButton'
import Button from '../../components/Button'
import GoogleMap from '../../components/GoogleMap'
import FavoriteButton from '../../components/FavoriteButton'
import Table from '../../components/Table'
import ImageSlider from './components/ImageSlider'
import SocialButtons from './components/SocialButtons'
import DataRow from './components/DataRow'
import PropertyLoadingPage from './components/PropertyLoadingPage'
import PropertyAgents from './components/PropertyAgents'
import Walkscore from './components/Walkscore'
import TourMiniForm from './components/TourMiniForm'

const Section = styled.section`
  margin-bottom: 2rem;
`

const HeaderContainer = styled.div`
  position: relative;
  text-align: center;
  margin-bottom: 2rem;

  &::after {
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    height: 1px;
    border-bottom: 2px solid rgb(220, 225, 230);
    content: '';
  }
`

const Header = styled.h3`
  display: inline-block;
  text-transform: uppercase;
  position: relative;
  background: rgb(240, 244, 247);
  padding: 0 2rem;
  z-index: 1;
`

const HeaderIcon = styled.span`
  margin-right: 8px;
  font-size: 90%;
`

type Props = {
  user: User,
  router: Object,
  alert: Function,
  property: Object,
  property: Object,
  isLoading: boolean,
  fetchProperty: Function,
  createFavorite: Function,
  deleteFavorite: Function,
  isAuthenticated: boolean,
  params: { mlsId: number },
  openContactModal: Function,
  favorites: Array<{ id: number, mls_id: number }>
}

class Property extends Component {
  componentDidMount() {
    this.props.fetchProperty(this.props.params.mlsId)
  }

  props: Props

  handleCreateFavorite = () =>
    this.props.createFavorite(this.props.user.id, this.props.property.mlsId)

  handleDeleteFavorite = () =>
    this.props.deleteFavorite(this.props.user.id, this.props.property.mlsId)

  handleUnauthenticatedFavorite = () =>
    this.props.alert({
      message: 'Please login to save favorites',
      klass: 'primary',
      timeout: 4000
    })

  handleUnauthenticatedOfferClick = () => {
    this.props.router.push('/login')
    this.props.alert({
      message: 'Please login to continue',
      klass: 'primary',
      timeout: 4000
    })
  }

  handleTourSubmit = (data) => {
    if (!this.props.isAuthenticated) {
      this.props.router.push('/login')
      this.props.alert({
        message: 'Please login to continue',
        klass: 'primary',
        timeout: 4000
      })
    } else {
      this.props.router.push({
        pathname: `/tour/${this.props.property.mlsId}`,
        query: { ...data }
      })
    }
  }

  render() {
    const { property, isLoading } = this.props
    if (isLoading || isEmpty(property)) return <PropertyLoadingPage />

    const rebate = rebateAmount(property.listPrice)
    const address = `${property.address.full}, ${property.address.city}, ${property.address.state} ${property.address.postalCode}`
    const favoriteMlsIds = this.props.favorites.map(
      (favorite) => favorite.mls_id
    )

    return (
      <div>
        <Helmet title={property.address ? property.address.full : ' '} />
        {property.photos && property.photos.length ? (
          <ImageSlider images={property.photos} />
        ) : null}
        <div
          style={{
            background: '#fff',
            padding: '2rem 0',
            borderBottom: '1px solid #e1e6eb'
          }}
        >
          <div className="container">
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                alignItems: 'flex-end'
              }}
            >
              <div>
                <h1>{property.address && property.address.full}</h1>
                {property.address && (
                  <div
                    style={{ color: 'rgb(160,165,170)', fontSize: '1.5rem' }}
                  >
                    {property.address.city}, {property.address.state}{' '}
                    {property.address.postalCode}
                  </div>
                )}
              </div>
              <div>
                <h2 style={{ color: colors.success }}>
                  {property.listPrice &&
                    `$${Number(property.listPrice).toLocaleString()}`}
                </h2>
                <SocialButtons />
              </div>
            </div>
          </div>
        </div>
        <div style={{ padding: '2rem 0' }}>
          <div className="container">
            {property.geo && property.address && property.property && (
              <div style={{ marginBottom: '2rem' }}>
                <span
                  className="fa fa-map-marker"
                  style={{ marginRight: '6px' }}
                />
                {property.geo.county && (
                  <Link to={`/properties?counties=${property.geo.county}`}>
                    {property.geo.county} County
                  </Link>
                )}
                {property.address.city && (
                  <span>
                    <span>&nbsp;&gt;&nbsp;</span>
                    <Link to={`/properties?cities=${property.address.city}`}>
                      {property.address.city}
                    </Link>
                  </span>
                )}
                {property.property.subdivision && (
                  <span>
                    <span>&nbsp;&gt;&nbsp;</span>
                    <Link
                      to={`/properties?neighborhoods=${property.property.subdivision}`}
                    >
                      {property.property.subdivision}
                    </Link>
                  </span>
                )}
              </div>
            )}
            <div className="row">
              <div className="col-lg-8 col-xs-12">
                <Section>
                  <HeaderContainer>
                    <Header>
                      <HeaderIcon className="fa fa-home" />
                      <span>Key Details</span>
                    </Header>
                  </HeaderContainer>
                  {property.property && (
                    <Table style={{ marginBottom: '3rem' }}>
                      <tbody>
                        <DataRow
                          label="Bedrooms"
                          data={property.property.bedrooms}
                        />
                        <DataRow
                          label="Bathrooms"
                          data={
                            <span>
                              <span>{property.property.bathsFull}</span>
                              {property.property.bathsHalf > 0 && (
                                <span>
                                  {' '}
                                  full / {property.property.bathsHalf} half
                                </span>
                              )}
                            </span>
                          }
                        />
                        {property.property.area > 0 && (
                          <DataRow
                            label="Living area"
                            data={`${Number(
                              property.property.area
                            ).toLocaleString()} sf`}
                          />
                        )}
                        <DataRow
                          label="Garage spaces"
                          data={property.property.garageSpaces}
                        />
                        <DataRow
                          label="Property taxes"
                          data={
                            property.tax && toUSD(property.tax.taxAnnualAmount)
                          }
                        />
                        <DataRow label="MLS number" data={property.listingId} />
                        <DataRow
                          label="Market time"
                          data={`${property.mls.daysOnMarket} ${pluralize(
                            property.mls.daysOnMarket,
                            'day'
                          )} on the market`}
                        />
                      </tbody>
                    </Table>
                  )}
                </Section>
                {property.property &&
                  property.property.interiorFeatures &&
                  property.property.interiorFeatures.length && (
                    <Section>
                      <HeaderContainer>
                        <Header>
                          <HeaderIcon className="fa fa-tags" />
                          <span>Features</span>
                        </Header>
                      </HeaderContainer>
                      <div className="row">
                        {property.property.interiorFeatures
                          .split(',')
                          .map((feature) => (
                            <div
                              key={feature}
                              className="col-sm-4 col-xs-12"
                              style={{ marginBottom: '.5rem' }}
                            >
                              {feature}
                            </div>
                          ))}
                      </div>
                    </Section>
                  )}
                <Section>
                  <HeaderContainer>
                    <Header>
                      <HeaderIcon className="fa fa-book" />
                      <span>Overview</span>
                    </Header>
                  </HeaderContainer>
                  <div style={{ marginBottom: '1rem' }}>{property.remarks}</div>
                  {property.office && (
                    <p style={{ fontSize: '14px' }}>
                      Listing courtesy of {property.office.name}
                    </p>
                  )}
                </Section>
                <Section>
                  <HeaderContainer>
                    <Header>
                      <HeaderIcon className="fa fa-info-circle" />
                      <span>Building</span>
                    </Header>
                  </HeaderContainer>
                  {property.property && (
                    <Table>
                      <tbody>
                        <DataRow label="Roof" data={property.property.roof} />
                        <DataRow label="Style" data={property.property.style} />
                        <DataRow
                          label="Construction"
                          data={padCommas(property.property.construction)}
                        />
                        <DataRow
                          label="Stories"
                          data={property.property.stories}
                        />
                        <DataRow
                          label="Fireplaces"
                          data={property.property.fireplaces}
                        />
                        <DataRow
                          label="Heating"
                          data={padCommas(property.property.heating)}
                        />
                        <DataRow
                          label="Cooling"
                          data={padCommas(property.property.cooling)}
                        />
                        <DataRow
                          label="Foundation"
                          data={property.property.foundation}
                        />
                        <DataRow
                          label="Laundry"
                          data={padCommas(property.property.laundryFeatures)}
                        />
                        <DataRow
                          label="Exterior features"
                          data={padCommas(property.property.exteriorFeatures)}
                        />
                        <DataRow
                          label="Lot size"
                          data={property.property.lotSize}
                        />
                      </tbody>
                    </Table>
                  )}
                </Section>
                {property.school &&
                  (property.school.highSchool ||
                    property.school.middleSchool ||
                    property.school.elementarySchool) && (
                    <Section>
                      <HeaderContainer>
                        <Header>
                          <HeaderIcon className="fa fa-graduation-cap" />
                          <span>Schools</span>
                        </Header>
                      </HeaderContainer>
                      <Table>
                        <tbody>
                          <DataRow
                            label="High School"
                            data={titleCase(property.school.highSchool)}
                          />
                          <DataRow
                            label="Middle School"
                            data={titleCase(property.school.middleSchool)}
                          />
                          <DataRow
                            label="Elementary School"
                            data={titleCase(property.school.elementarySchool)}
                          />
                        </tbody>
                      </Table>
                    </Section>
                  )}
              </div>
              <div className="col-lg-4 col-xs-12">
                <Card style={{ marginBottom: '2rem' }}>
                  {rebate > 0 && (
                    <p style={{ fontWeight: 'bold', textAlign: 'center' }}>
                      Get a <RebateButton rebate={rebate} /> rebate when you
                      purchase with Kief Realty.
                    </p>
                  )}
                  {this.props.isAuthenticated ? (
                    <Button
                      color="primary"
                      to={`/offer/${property.mlsId}`}
                      style={{
                        display: 'block',
                        width: '100%',
                        marginBottom: '1rem'
                      }}
                    >
                      Make an Offer
                    </Button>
                  ) : (
                    <Button
                      color="primary"
                      onClick={this.handleUnauthenticatedOfferClick}
                      style={{
                        display: 'block',
                        width: '100%',
                        marginBottom: '1rem'
                      }}
                    >
                      Make an Offer
                    </Button>
                  )}
                  <Button
                    onClick={this.props.openContactModal}
                    style={{ display: 'block', width: '100%' }}
                  >
                    Contact us
                  </Button>
                  <hr />
                  <div style={{ textAlign: 'center' }}>
                    <FavoriteButton
                      onCreate={this.handleCreateFavorite}
                      onDelete={this.handleDeleteFavorite}
                      isAuthenticated={this.props.isAuthenticated}
                      isFavorite={includes(favoriteMlsIds, property.mlsId)}
                      onUnauthenticatedClick={
                        this.handleUnauthenticatedFavorite
                      }
                    />
                  </div>
                </Card>
                <PropertyAgents
                  openContactModal={this.props.openContactModal}
                  propertyAgentId={get(property, 'agent.id')}
                />
                <TourMiniForm
                  onSubmit={this.handleTourSubmit}
                  initialValues={{ time: '12:00PM', method: 'In Person' }}
                />
                <GoogleMap
                  address={address}
                  lat={property.geo && property.geo.lat}
                  lng={property.geo && property.geo.lng}
                  style={{ marginBottom: '2rem' }}
                />
                <Walkscore
                  address={address}
                  lat={property.geo && property.geo.lat}
                  lng={property.geo && property.geo.lng}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }
}

export default connect(
  (state) => ({
    user: state.session.currentUser,
    property: state.properties.current,
    favorites: state.user.favorites,
    isLoading: state.properties.isLoadingCurrent,
    isAuthenticated: state.session.isAuthenticated
  }),
  {
    alert,
    fetchProperty,
    createFavorite,
    deleteFavorite,
    openContactModal
  }
)(Property)



// WEBPACK FOOTER //
// ./src/views/Property/index.js